import React, { useEffect, useMemo, useRef } from 'react';
import DataTable from 'datatables.net-bs5';
import 'datatables.net-bs5/css/dataTables.bootstrap5.min.css';
import { icon } from '@fortawesome/fontawesome-svg-core';
import {
  faCircleXmark,
  faCircleExclamation,
  faCircleCheck,
  faCircleInfo,
} from '@fortawesome/free-solid-svg-icons';

const PAGE_LENGTH_OPTIONS = [20, 50, 100, -1];
const PAGE_LENGTH_LABELS = [20, 50, 100, 'All'];

const STATUS_SORT = { critical: 0, major: 1, minor: 2, info: 3, ok: 4 };

const STATUS_ICON_BY_KEY = {
  critical: faCircleXmark,
  major: faCircleExclamation,
  minor: faCircleCheck,
  info: faCircleInfo,
  ok: faCircleCheck,
};

const STATUS_CLASS_BY_KEY = {
  critical: 'red',
  major: 'yellow',
  minor: 'green',
  info: 'blue',
  ok: 'green',
};

function destroyDataTable(dataTableRef, restore = true) {
  if (!dataTableRef.current) {
    return;
  }

  try {
    dataTableRef.current.destroy(restore);
  } catch (error) {
    console.warn('Failed to destroy DataTable instance:', error);
  } finally {
    dataTableRef.current = null;
  }
}

function resetTableHost(tableHostRef, dataTableRef) {
  destroyDataTable(dataTableRef);

  if (tableHostRef.current) {
    tableHostRef.current.innerHTML = '';
  }
}

function formatTableInfo(_settings, start, end, _max, total) {
  if (total === 0) {
    return 'Showing 0 to 0 out of 0 entries';
  }

  return `Showing ${start} to ${end} out of ${total} entries`;
}

function normalizeStatus(status) {
  return String(status ?? '').trim().toLowerCase();
}

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

function faultCodeColumn() {
  return {
    title: 'Fault Code',
    className: 'alarms-table__fault-code',
    render: (cellData, type, row) => {
      const code = cellData ?? '';
      const description = row?.[5] ?? '';
      if (type === 'sort' || type === 'type' || type === 'filter') {
        return code;
      }
      if (!code) {
        return '—';
      }
      if (!description) {
        return escapeHtml(code);
      }
      return (
        `<span class="fault-code-link" tabindex="0">`
        + escapeHtml(code)
        + `<span class="fault-code-tooltip" role="tooltip">${escapeHtml(description)}</span>`
        + `</span>`
      );
    },
  };
}

function textColumn(title) {
  return {
    title,
    render: (cellData, type) => {
      if (type === 'sort' || type === 'type') {
        return cellData ?? '';
      }

      return cellData === null || cellData === undefined || cellData === '' ? '—' : cellData;
    },
  };
}

function isActiveAlarm(value) {
  if (value === true || value === 1) {
    return true;
  }
  if (value === false || value === 0 || value == null || value === '') {
    return false;
  }
  return String(value).trim().toLowerCase() === 'true';
}

function stateColumn() {
  return {
    title: 'State',
    render: (cellData, type) => {
      const active = isActiveAlarm(cellData);
      if (type === 'sort' || type === 'type') {
        return active ? 0 : 1;
      }
      return active ? 'Active' : 'Cleared';
    },
  };
}

function statusColumn() {
  return {
    title: 'Status',
    className: 'dt-left alarms-table__status',
    render: (cellData, type) => {
      if (type === 'sort' || type === 'type') {
        const key = normalizeStatus(cellData);
        return STATUS_SORT[key] ?? 5;
      }

      return '';
    },
    createdCell: (cell, cellData) => {
      const key = normalizeStatus(cellData);
      if (!key) {
        cell.textContent = '—';
        return;
      }
      const faIcon = STATUS_ICON_BY_KEY[key] ?? faCircleExclamation;
      const statusClass = STATUS_CLASS_BY_KEY[key] ?? '';
      const rendered = icon(faIcon);
      cell.innerHTML = rendered.html[0];
      const svg = cell.querySelector('svg');
      if (svg && statusClass) {
        svg.classList.add(statusClass);
      }
    },
  };
}

function AlarmsTable({ data = [] }) {
  const tableHostRef = useRef(null);
  const dataTableRef = useRef(null);

  const tableRows = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  useEffect(() => {
    const host = tableHostRef.current;
    if (!host || tableRows.length === 0) {
      return undefined;
    }

    resetTableHost(tableHostRef, dataTableRef);

    const table = document.createElement('table');
    table.className = 'table table-striped w-100 alarms-table';
    table.style.width = '100%';
    host.appendChild(table);

    const columns = [
      { data: 0, ...statusColumn() },
      { data: 1, ...stateColumn() },
      { data: 2, ...textColumn('PB') },
      { data: 3, ...faultCodeColumn() },
      { data: 4, ...textColumn('Date Raised') },
      { data: 5, title: 'Description', visible: false, searchable: false },
    ];

    const tableData = tableRows.map((alarm) => [
      alarm?.status ?? alarm?.severity ?? '',
      alarm?.is_active ?? alarm?.isActive,
      alarm?.pb,
      alarm?.faultCode ?? alarm?.fault_code ?? '',
      alarm?.dateRaised ?? alarm?.date_raised,
      alarm?.description ?? '',
    ]);

    dataTableRef.current = new DataTable(table, {
      data: tableData,
      columns,
      pageLength: 20,
      lengthMenu: [PAGE_LENGTH_OPTIONS, PAGE_LENGTH_LABELS],
      searching: true,
      paging: true,
      ordering: true,
      autoWidth: false,
      initComplete: () => {
        host.querySelectorAll('table.alarms-table').forEach((el) => {
          el.style.width = '100%';
        });
        host.querySelectorAll('thead th').forEach((th) => {
          th.style.removeProperty('width');
          th.style.removeProperty('min-width');
          th.style.removeProperty('max-width');
        });
        dataTableRef.current?.columns.adjust().draw(false);
      },
      layout: {
        topStart: 'pageLength',
        topEnd: {
          search: {
            placeholder: 'Search table...',
          },
        },
        bottomStart: {
          info: {
            callback: formatTableInfo,
          },
        },
        bottomEnd: {
          paging: {
            numbers: 5,
          },
        },
      },
    });

    return () => {
      resetTableHost(tableHostRef, dataTableRef);
    };
  }, [tableRows]);

  useEffect(() => () => {
    resetTableHost(tableHostRef, dataTableRef);
  }, []);

  return (
    <>
      {tableRows.length === 0 && (
        <p className="mb-2">No data available</p>
      )}
      <div
        ref={tableHostRef}
        className="alarms-table-host"
      />
    </>
  );
}

export default AlarmsTable;
